import { useMutation } from '@tanstack/react-query';
import type { AuthUser } from '@/store/auth';
import { apiPost } from './_base';
import type { TgOAuthData } from '@/lib/tgOauth';
import { buildDataCheckString } from '@/lib/tgOauth';

// Backend error code for "Telegram identity verified but no local account
// exists yet; resend with a password to create one." Resolves from
// services/users/internal/pkg/stderr/codes.go: telegramOAuth_NeedsPasswordSetup
// (iota 169) + UsersServiceErrCode (9001) = 9170. If the backend enum is
// reordered, this constant must move in lockstep.
export const TG_OAUTH_NEEDS_PASSWORD_CODE = 9170;

/**
 * GeeTest's widget result, carried on the auth request.
 *
 * hCaptcha has no equivalent here on purpose: /login and /register do not
 * accept its token. It is confirmed out-of-band against
 * /api/v1/user/captcha/hcaptcha/verify before the form is submitted.
 */
export type GeetestFields = {
  geetest_challenge?: string;
  geetest_validate?: string;
  geetest_seccode?: string;
};

export type SignInPayload = { account: string; password: string; remember?: boolean; code?: string } & GeetestFields;
export type SignUpPayload = {
  account: string;
  password: string;
  confirm: string;
  /** Telegram username or phone number sent as-is to the backend. */
  telegram_info?: string;
  invitation?: string;
} & GeetestFields;
export type TgQuickPayload = {
  account: string;
  password: string;
  confirm: string;
  invitation?: string;
  // Raw signed initData blob from window.Telegram.WebApp.initData. The
  // /telegram/sign-up endpoint's TelegramVerifyMiddleware HMAC-verifies
  // this and derives the canonical TG identity from it.
  initData: string;
};
export type TelegramMiniAppAuthPayload = {
  initData: string;
};

type AuthResult = { user: AuthUser; token: string; refreshToken?: string; betToken?: string; passwordWeak?: boolean; blockedProviders: string[] };

type LoginUserRequest = {
  account?: string;
  email?: string;
  phone?: string;
  password: string;
  code?: string;
};

type ApiUser = {
  id?: string;
  username?: string;
  account?: string;
  email?: string;
  phone?: string;
  image?: string;
  providers?: string;
};

type LoginUserResponse = {
  token: string;
  refresh_token?: string;
  bet_token?: string;
  user: ApiUser;
  // True when the password just used is one the server would refuse if it were
  // being set today. Optional because a server predating the field omits it —
  // absent reads as "nothing to report", which is the safe default.
  password_weak?: boolean;
};

function toAuthUser(u: ApiUser): AuthUser {
  return {
    id: u.id ?? '',
    username: u.username ?? u.email ?? u.phone ?? '',
    account: u.account ?? '',
    avatar: u.image,
  };
}

async function signIn(p: SignInPayload): Promise<AuthResult> {
  const body: LoginUserRequest = {
    account: p.account.trim(),
    password: p.password,
    ...(p.code ? { code: p.code } : {}),
    ...(p.geetest_challenge ? { geetest_challenge: p.geetest_challenge, geetest_validate: p.geetest_validate, geetest_seccode: p.geetest_seccode } : {}),
  };
  const res = await apiPost<LoginUserResponse>('/api/v1/login', body);
  return {
    user: toAuthUser(res.user),
    token: res.token,
    refreshToken: res.refresh_token,
    betToken: res.bet_token,
    passwordWeak: res.password_weak === true,
    blockedProviders: res.user.providers ? res.user.providers.split(',').map((p) => p.trim()).filter(Boolean) : [],
  };
}

async function signUp(p: SignUpPayload): Promise<AuthResult> {
  if (p.password !== p.confirm) throw new Error('Passwords do not match');

  const body = {
    account: p.account.trim(),
    telegram_info: p.telegram_info?.trim() ?? '',
    password: p.password,
    referred_code: p.invitation ?? '',
    // welcome_wheel_hash has no `,optional` tag server-side — must be present.
    welcome_wheel_hash: '',
    ...(p.geetest_challenge ? { geetest_challenge: p.geetest_challenge, geetest_validate: p.geetest_validate, geetest_seccode: p.geetest_seccode } : {}),
  };
  const res = await apiPost<LoginUserResponse>('/api/v1/register', body);
  return {
    user: toAuthUser(res.user),
    token: res.token,
    refreshToken: res.refresh_token,
    betToken: res.bet_token,
    blockedProviders: res.user.providers ? res.user.providers.split(',').map((p) => p.trim()).filter(Boolean) : [],
  };
}

async function tgQuick(p: TgQuickPayload): Promise<AuthResult> {
  if (p.password !== p.confirm) throw new Error('Passwords do not match');
  if (!p.initData) throw new Error('Telegram session not available');
  // POST to /telegram/sign-up (behind TelegramVerifyMiddleware) so the
  // server authoritatively derives TG identity from the verified initData
  // payload. The middleware reads `data` from the body, HMAC-verifies it
  // with the bot token, and injects the parsed TG user into the request
  // metadata before the handler runs. The previously-used /telegram/
  // register endpoint required a pre-existing telegram_app_users row that
  // no public flow creates, so every submit failed.
  const body = {
    data: p.initData,
    account: p.account.trim(),
    password: p.password,
    referred_code: p.invitation ?? '',
    phone: '',
    username: p.account.trim(),
  };
  const res = await apiPost<LoginUserResponse>('/api/v1/telegram/sign-up', body);
  return {
    user: toAuthUser(res.user),
    token: res.token,
    refreshToken: res.refresh_token,
    betToken: res.bet_token,
    blockedProviders: res.user.providers ? res.user.providers.split(',').map((p) => p.trim()).filter(Boolean) : [],
  };
}

export function useSignIn() {
  return useMutation({ mutationFn: signIn });
}

export function useSignUp() {
  return useMutation({ mutationFn: signUp });
}

export function useTgQuick() {
  return useMutation({ mutationFn: tgQuick });
}

// --- Telegram Mini App auto-login -----------------------------------------
//
// The Telegram landing popup calls this when the user taps "TG Quick Sign In".
// Backend HMAC-verifies the signed initData and returns a normal auth payload
// for existing Telegram users. Per product flow, HTTP 401 means "first-time
// Telegram user" and routes to the Telegram signup screen.
export type MiniAppAuthPayload = TelegramMiniAppAuthPayload & { invitation?: string };

const TELEGRAM_MINIAPP_AUTH_PATH =
  import.meta.env.VITE_TELEGRAM_MINIAPP_AUTH_PATH ?? '/api/v1/telegram/login-with-signature';

function shouldLogTelegramInitData() {
  if (!import.meta.env.DEV) return false;
  try {
    return localStorage.getItem('hg_log_tg_initdata') === '1';
  } catch {
    return false;
  }
}

async function miniAppAuth(p: MiniAppAuthPayload): Promise<AuthResult> {
  if (!p.initData.trim()) throw new Error('Telegram init data is missing');
  if (shouldLogTelegramInitData()) {
    const params = new URLSearchParams(p.initData);
    console.log('Telegram raw initData:', p.initData);
    console.log('Telegram auth_date:', params.get('auth_date'));
    console.log('Telegram user:', params.get('user'));
    console.log('Telegram hash:', params.get('hash'));
  }
  const res = await apiPost<LoginUserResponse>(TELEGRAM_MINIAPP_AUTH_PATH, {
    data: p.initData,
    // Referral linkage for the auto-create path: when the backend creates a
    // first-time Telegram user on this call, `start_param` associates them with
    // the referrer. (The /telegram/sign-up completion path uses `referred_code`
    // instead.)
    ...(p.invitation ? { start_param: p.invitation } : {}),
  });
  return {
    user: toAuthUser(res.user),
    token: res.token,
    refreshToken: res.refresh_token,
    betToken: res.bet_token,
    blockedProviders: res.user.providers ? res.user.providers.split(',').map((p) => p.trim()).filter(Boolean) : [],
  };
}

export function useTelegramMiniAppAuth() {
  return useMutation({ mutationFn: miniAppAuth });
}

export const telegramMiniAppAuth = miniAppAuth;

// --- Telegram OAuth (browser path) ---------------------------------------
//
// One endpoint serves two phases:
//   1. Verify-only:    oauthTelegram(tgData)
//      - existing user: returns AuthResult (caller signs in immediately)
//      - new user:      backend returns 4xx with code TG_OAUTH_NEEDS_PASSWORD_CODE
//                       (caller routes to the "set password" page and re-calls
//                       with password)
//   2. Create-account: oauthTelegram(tgData, password, invitation?)

export type OauthTelegramPayload = {
  data: TgOAuthData;
  password?: string;
  invitation?: string;
};

async function oauthTelegram(p: OauthTelegramPayload): Promise<AuthResult> {
  const body = {
    id: String(p.data.id),
    first_name: p.data.first_name,
    last_name: p.data.last_name ?? '',
    username: p.data.username ?? '',
    hash: p.data.hash,
    payload: buildDataCheckString(p.data),
    password: p.password ?? '',
    referred_code: p.invitation ?? '',
  };
  const res = await apiPost<LoginUserResponse>('/api/v1/user/oauth/telegram', body);
  return {
    user: toAuthUser(res.user),
    token: res.token,
    refreshToken: res.refresh_token,
    betToken: res.bet_token,
    blockedProviders: res.user.providers ? res.user.providers.split(',').map((p) => p.trim()).filter(Boolean) : [],
  };
}

export function useOauthTelegram() {
  return useMutation({ mutationFn: oauthTelegram });
}
